import type { DataStream, MP4ArrayBuffer, MP4File, MP4Info, MP4Sample, MP4VideoTrack } from 'mp4box'

export interface DemuxedSample {
  /** Presentation timestamp in microseconds. */
  timestamp: number
  duration: number
  key: boolean
  data: Uint8Array
}

export interface DemuxedVideo {
  config: VideoDecoderConfig
  /**
   * Encoded chunks in **decode order** — the order `VideoDecoder` must be fed.
   * With B-frames this is not presentation order, so never sort this list.
   */
  samples: DemuxedSample[]
  /** Indices into `samples` of every sync sample, ascending. */
  keyframes: number[]
  width: number
  height: number
}

/**
 * Read an entire MP4 into a random-accessible list of encoded chunks.
 *
 * The whole file is buffered deliberately. It is a 24-second loop that will be
 * played end to end and seeked within constantly, so streaming it would mean
 * re-fetching the same ranges repeatedly; holding the compressed bytes costs
 * no more than the download already did and makes every seek instant.
 */
export async function demuxVideo(url: string, signal?: AbortSignal): Promise<DemuxedVideo> {
  // mp4box is ~140 kB and is only needed on the WebCodecs path, so it is loaded
  // on demand rather than shipped in the initial bundle.
  const MP4Box = await import('mp4box')

  let res = await fetch(url, { signal: signal ?? null, cache: 'force-cache' })
  if (!res.ok) {
    // See audio.ts: an error response cached under the immutable rule must
    // not be allowed to pin force-cache to the failure. Retry punching
    // through the cache before giving up.
    res = await fetch(url, { signal: signal ?? null, cache: 'reload' })
  }
  if (!res.ok) throw new Error(`Could not load video ${url} (${res.status})`)
  const bytes = await res.arrayBuffer()

  return new Promise<DemuxedVideo>((resolve, reject) => {
    const file = MP4Box.createFile()
    const samples: DemuxedSample[] = []
    let track: MP4VideoTrack | undefined
    let description: Uint8Array | undefined

    file.onError = (err) => reject(new Error(`MP4 parse failed: ${err}`))

    file.onReady = (info: MP4Info) => {
      track = info.videoTracks[0]
      if (!track) {
        reject(new Error('No video track in file.'))
        return
      }
      description = extractDescription(file, track.id, MP4Box.DataStream)
      file.setExtractionOptions(track.id, null, { nbSamples: track.nb_samples })
      file.start()
    }

    file.onSamples = (_id, _user, batch: MP4Sample[]) => {
      for (const s of batch) {
        samples.push({
          // mp4box reports timestamps in the track timescale; WebCodecs wants
          // microseconds.
          timestamp: Math.round((s.cts / s.timescale) * 1e6),
          duration: Math.round((s.duration / s.timescale) * 1e6),
          key: s.is_sync,
          data: s.data,
        })
      }
    }

    const buffer = bytes as MP4ArrayBuffer
    buffer.fileStart = 0
    file.appendBuffer(buffer)
    file.flush()

    if (!track) {
      reject(new Error('MP4 contained no readable video track.'))
      return
    }
    if (!description) {
      reject(new Error('Could not read codec configuration from the MP4.'))
      return
    }

    normalisePresentation(samples)

    // `samples` is deliberately left in decode order. Sorting it by timestamp
    // would look tidier and would break every B-frame stream, because a decoder
    // fed out of decode order cannot resolve forward references.
    const keyframes: number[] = []
    for (let i = 0; i < samples.length; i++) if (samples[i]!.key) keyframes.push(i)
    if (keyframes.length === 0) {
      reject(new Error('MP4 contained no sync samples; cannot seek.'))
      return
    }

    resolve({
      config: {
        codec: track.codec,
        codedWidth: track.video.width,
        codedHeight: track.video.height,
        description,
        optimizeForLatency: true,
      },
      samples,
      keyframes,
      width: track.video.width,
      height: track.video.height,
    })
  })
}

/**
 * Shift presentation timestamps so the earliest one is zero.
 *
 * x264/x265 delay every presentation timestamp by the B-frame reorder depth —
 * two frames at these settings — and record the compensation in an edit list
 * that tells players to skip the lead-in. This demuxer reads raw sample
 * timestamps and never sees the edit list, so without this correction every
 * frame maps to an index two frames late and the picture trails the audio by
 * 33 ms: a percussion hit is *heard* before it is *seen*. AV1 has no reorder
 * delay and is unaffected, which is why the lag depended on which tier a
 * browser picked. Normalising to the earliest timestamp applies exactly the
 * constant shift the edit list encodes, without parsing `elst` box variants.
 */
export function normalisePresentation(samples: DemuxedSample[]): void {
  let earliest = Infinity
  for (const s of samples) if (s.timestamp < earliest) earliest = s.timestamp
  if (!Number.isFinite(earliest) || earliest === 0) return
  for (const s of samples) s.timestamp -= earliest
}

/**
 * Pull the raw `avcC` / `hvcC` / `av1C` payload out of the sample description.
 * WebCodecs wants the box contents without the eight-byte box header.
 */
function extractDescription(
  file: MP4File,
  trackId: number,
  DataStreamCtor: typeof DataStream,
): Uint8Array | undefined {
  const trak = file.getTrackById(trackId) as
    | { mdia?: { minf?: { stbl?: { stsd?: { entries?: Array<Record<string, unknown>> } } } } }
    | undefined

  const entries = trak?.mdia?.minf?.stbl?.stsd?.entries ?? []
  for (const entry of entries) {
    const box = (entry.avcC ?? entry.hvcC ?? entry.av1C ?? entry.vpcC) as
      | { write(stream: DataStream): void }
      | undefined
    if (!box) continue
    const stream = new DataStreamCtor(undefined, 0, DataStreamCtor.BIG_ENDIAN)
    box.write(stream)
    return new Uint8Array(stream.buffer.slice(8))
  }
  return undefined
}
